import { useLocation, useNavigate } from 'react-router';
import GreenCheckIcon from '../../assets/icons/check-green-icon.svg?react';
import BlueLinkIcon from '../../assets/icons/blue-link-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import clsx from 'clsx';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef } from 'react';
import { useApiClients } from '../../client/api/useApiClient';
import Loader from '../../components/Loader';
import { RequestAccessDto } from '@EHSNavigator/backend';

export default function OnboardPayment() {
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [requestMessage, setRequestMessage] = useState('');
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const uid = query.get('uid');
  const requestAttempted = useRef(false);

  const { OnboardOrganizationApi } = useApiClients();

  const requestAccessMutation = useMutation<any, Error, RequestAccessDto>({
    mutationFn: async (data: RequestAccessDto) => {
      const response = await OnboardOrganizationApi.onboardOrganizationControllerRequestAccess({
        requestAccessDto: {
          uid: data.uid
        },
      });
      return response.data;
    },

    onSuccess: (data) => {
      toast.success(data?.message || 'Access request submitted successfully');
      setIsLoading(false);
      setRequestMessage(data?.message || 'Your access request has been submitted successfully');
      setHasError(false);
    },
    onError: (error: any) => {
      setIsLoading(false);
      setHasError(true);
      const errorMessage = error?.response?.data?.message;
      if (errorMessage?.includes('expired') || errorMessage?.includes('invalid')) {
        setIsExpired(true);
      }
      if (!Array.isArray(errorMessage)) {
        setRequestMessage(errorMessage || 'An unexpected error occurred.');
      } else {
        setRequestMessage('An unexpected error occurred.');
      }
    },
  });

  useEffect(() => {
    if (uid && !requestAttempted.current) {
      requestAttempted.current = true;
      requestAccessMutation.mutate({
        uid: String(uid),
      });
    } else if (!uid) {
      setIsLoading(false);
      setIsExpired(true);
    }
  }, [uid, requestAccessMutation]);

  const buttonSize = useButtonSizeLarge();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <main className="relative z-10 flex flex-1 flex-col items-center justify-between gap-10 pb-5">
      <div className="flex flex-1 flex-col justify-center">
        {isExpired ? (
          <div className="mx-auto flex max-w-685 flex-col items-center gap-12 px-25">
            <BlueLinkIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
            <div className="flex flex-col gap-2.5 text-center">
              <h2 className={clsx('text-neutral-tones-text font-bold')}>
                Link Expired or Invalid
              </h2>
              <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
                {requestMessage || 'This payment link is no longer valid. Please contact support for assistance.'}
              </p>
            </div>
            <Button
              onClick={() => navigate('/sign-in')}
              otherClasses={clsx('max-w-465 w-full')}
              size={buttonSize}
            >
              Go to Sign In
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-12 px-25">
            {hasError ? (
              <WarningIcon className="[&>g>path]:fill-brand-posh-pink h-100 w-100 lg:h-150 lg:w-150" />
            ) : (
              <GreenCheckIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
            )}
            <div className="flex flex-col gap-2.5 text-center">
              <h2 className={clsx('font-bold', hasError ? 'text-brand-posh-pink' : 'text-neutral-tones-text')}>
                {requestMessage || (hasError ? 'Error Occurred' : 'Access Request Submitted')}
              </h2>
              {!hasError && (
                <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
                  Your organization access request has been submitted successfully. You will be notified once your request is reviewed.
                </p>
              )}
            </div>
            <Button
              onClick={() => navigate('/sign-in')}
              otherClasses={clsx('max-w-465 w-full', hasError && 'bg-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink')}
              size={buttonSize}
            >
              Sign In
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}

